type EventCallback = (...args: unknown[]) => unknown;

interface NamespaceMap {
  [event: string]: EventCallback[];
}

type CallbackRegistry = Record<string, NamespaceMap>;

interface ResolvedName {
  original: string;
  value: string;
  namespace: string;
}

export default class EventEmitter {
  private callbacks: CallbackRegistry = {
    base: {},
  };

  on(names: string, callback: EventCallback): this | false {
    if (!names) {
      console.warn("wrong names");
      return false;
    }

    if (typeof callback === "undefined") {
      console.warn("wrong callback");
      return false;
    }

    const resolvedNames = this.resolveNames(names);

    resolvedNames.forEach((_name) => {
      const name = this.resolveName(_name);

      if (!(this.callbacks[name.namespace] instanceof Object)) {
        this.callbacks[name.namespace] = {};
      }

      if (!(this.callbacks[name.namespace][name.value] instanceof Array)) {
        this.callbacks[name.namespace][name.value] = [];
      }

      this.callbacks[name.namespace][name.value].push(callback);
    });

    return this;
  }

  off(names: string): this | false {
    if (!names) {
      console.warn("wrong name");
      return false;
    }

    const resolvedNames = this.resolveNames(names);

    resolvedNames.forEach((_name) => {
      const name = this.resolveName(_name);

      if (name.namespace !== "base" && name.value === "") {
        delete this.callbacks[name.namespace];
      } else {
        if (name.namespace === "base") {
          Object.keys(this.callbacks).forEach((namespace) => {
            const namespaceCallbacks = this.callbacks[namespace];

            if (
              namespaceCallbacks instanceof Object &&
              namespaceCallbacks[name.value] instanceof Array
            ) {
              delete namespaceCallbacks[name.value];

              if (Object.keys(namespaceCallbacks).length === 0) {
                delete this.callbacks[namespace];
              }
            }
          });
        } else {
          const namespaceCallbacks = this.callbacks[name.namespace];

          if (
            namespaceCallbacks instanceof Object &&
            namespaceCallbacks[name.value] instanceof Array
          ) {
            delete namespaceCallbacks[name.value];

            if (Object.keys(namespaceCallbacks).length === 0) {
              delete this.callbacks[name.namespace];
            }
          }
        }
      }
    });

    return this;
  }

  trigger(nameInput: string, argsInput?: unknown[]): unknown {
    if (!nameInput) {
      console.warn("wrong name");
      return false;
    }

    let finalResult: unknown = undefined;

    const args = Array.isArray(argsInput) ? argsInput : [];
    const names = this.resolveNames(nameInput);
    const name = this.resolveName(names[0]);

    if (name.namespace === "base") {
      Object.keys(this.callbacks).forEach((namespace) => {
        const namespaceCallbacks = this.callbacks[namespace];

        if (
          namespaceCallbacks instanceof Object &&
          namespaceCallbacks[name.value] instanceof Array
        ) {
          namespaceCallbacks[name.value].forEach((callback) => {
            const result = callback(...args);

            if (typeof finalResult === "undefined") {
              finalResult = result;
            }
          });
        }
      });
    } else if (this.callbacks[name.namespace] instanceof Object) {
      const namespaceCallbacks = this.callbacks[name.namespace];

      if (name.value === "") {
        console.warn("wrong name");
        return this;
      }

      namespaceCallbacks[name.value].forEach((callback) => {
        const result = callback(...args);

        if (typeof finalResult === "undefined") {
          finalResult = result;
        }
      });
    }

    return finalResult;
  }

  private resolveNames(names: string): string[] {
    return names
      .replace(/[^a-zA-Z0-9 ,/.]/g, "")
      .replace(/[,/]+/g, " ")
      .split(" ");
  }

  private resolveName(name: string): ResolvedName {
    const parts = name.split(".");

    return {
      original: name,
      value: parts[0],
      namespace: parts.length > 1 && parts[1] !== "" ? parts[1] : "base",
    };
  }
}
